import { Component, DestroyRef, input, OnInit, inject } from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Valuator } from '@app/core/stingray-api/models';
import { finalize, map } from 'rxjs/operators';
import { requireMatch } from '@app/validators/autocomplete-validator';
import { ValuatorAutoCompleteService } from './valuator-autocomplete.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { includes } from 'lodash';
import { MatListItem } from '@angular/material/list';
import { TranslateModule } from '@ngx-translate/core';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';


@Component({
    selector: 'unite-valuator-autocomplete',
    templateUrl: './valuator-autocomplete.component.html',
    styleUrls: ['./valuator-autocomplete.component.scss'],
    imports: [MatFormField, MatLabel, MatIconButton, MatSuffix, MatIcon, MatProgressSpinner, MatInput, ReactiveFormsModule, MatAutocompleteTrigger, MatAutocomplete, MatOption, TranslateModule, MatListItem]
})
export class ValuatorAutocompleteComponent implements OnInit {

  private destroyRef = inject(DestroyRef);
  control = input<FormControl>();
  required = input(false);
  focus = input(false);
  label = input<string>();
  withConnection = input(false);
  isMatListItem = input(false);

  valuators: Valuator[] = [];
  valuatorsFiltered: Valuator[];

  isLoadingValuators = false;

  constructor(
    private valuatorAutoCompleteService: ValuatorAutoCompleteService) { }

  ngOnInit() {
    if (this.required() === false) {
      this.control().setValidators([requireMatch]);
    } else {
      this.control().setValidators([Validators.required, requireMatch]);
    }

    this.isLoadingValuators = true;
    this.valuatorAutoCompleteService.getValuators().pipe(
      finalize(() => {
        this.isLoadingValuators = false;
      })
    ).subscribe((valuators: Valuator[]) => {
      this.valuators = valuators.filter(x => !this.withConnection() || x.hasConnection);
      this.valuatorsFiltered = this.valuators;
    });

    this.control().valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      map((value: string | Valuator) => {
        if (this.isValuator(value)) {
          return this.filterValuator(value.name);
        } else {
          return this.filterValuator(value || '');
        }
      })
    ).subscribe((valuators: Valuator[]) => {
      this.valuatorsFiltered = valuators;
    });
  }

  refreshValuators() {
    this.isLoadingValuators = true;
    this.valuatorAutoCompleteService.getValuators(true).pipe(
      finalize(() => {
        this.isLoadingValuators = false;
      })
    ).subscribe((valuators: Valuator[]) => {
        this.valuators = valuators.filter(x => !this.withConnection() || x.hasConnection);
        this.valuatorsFiltered = this.valuators;
    });
  }

  displayWithValuator(valuator: Valuator): string {
    if (valuator != null) {
      return valuator.name + ' - ' + valuator.address.city;
    } else {
      return '';
    }
  }

  filterValuator(value: string): Valuator[] {
    const filterValue = value.toLowerCase();

    return this.valuators.filter((valuator: Valuator) => {
      return this.displayWithValuator(valuator).toLowerCase().includes(filterValue) ||
            valuator.address.addressLine1.toLowerCase().includes(filterValue) ||
            valuator.address.addressLine2.toLowerCase().includes(filterValue)
    });
  }

  isValuator(value: any | Valuator): value is Valuator {
    return value != null && (<Valuator>value).id !== undefined;
  }
}
